import { useCallback, useState } from 'react';

import {
  useIsUserUnderMinimumAge,
  useUserMinimumAge,
} from '~/stores/userStore';

const DEFAULT_MINIMUM_AGE = 18;

function normalizeMinimumAge(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    return DEFAULT_MINIMUM_AGE;
  }

  return value;
}

type TUseMinimumAgeGuardResult = {
  isUnderMinimumAge: boolean;
  minimumAge: number;
  isMinimumAgeModalOpen: boolean;
  closeMinimumAgeModal: () => void;
  showMinimumAgeModal: () => void;
  runIfAgeAllowed: (action: () => void) => void;
  ensureAgeAllowed: () => boolean;
};

export function useMinimumAgeGuard(): TUseMinimumAgeGuardResult {
  const isUnderMinimumAge = useIsUserUnderMinimumAge();
  const minimumAgeRaw = useUserMinimumAge();

  const [isMinimumAgeModalOpen, setIsMinimumAgeModalOpen] = useState(false);

  const closeMinimumAgeModal = useCallback(() => {
    setIsMinimumAgeModalOpen(false);
  }, []);

  const showMinimumAgeModal = useCallback(() => {
    setIsMinimumAgeModalOpen(true);
  }, []);

  const runIfAgeAllowed = useCallback(
    (action: () => void) => {
      if (isUnderMinimumAge) {
        setIsMinimumAgeModalOpen(true);
        return;
      }

      action();
    },
    [isUnderMinimumAge],
  );

  const ensureAgeAllowed = useCallback(() => {
    if (isUnderMinimumAge) {
      setIsMinimumAgeModalOpen(true);
      return false;
    }

    return true;
  }, [isUnderMinimumAge]);

  return {
    isUnderMinimumAge,
    minimumAge: normalizeMinimumAge(minimumAgeRaw),
    isMinimumAgeModalOpen,
    closeMinimumAgeModal,
    showMinimumAgeModal,
    runIfAgeAllowed,
    ensureAgeAllowed,
  };
}
